import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import BusinessBreakdownDialog from "./bussinessBreakdown.js";
import ReceivedBusinessBreakdownDialog from "./ReceivedBusinessBreakdownDialog.js";
import ConnectorPaymentsDialog from "./connectorPaymentsDialog.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import AdditionalPayoutsDialog from "./AdditionalPayoutsDialog.js";
import RecoveriesDialog from "./RecoveriesDailog.js";

const BusinessSummaryBar = ({
  data,
  startDate,
  endDate,
  isIncomeByMis,
  setAlert,
  selectedDsaId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [controller] = useMaterialUIController();
  const { user } = controller;

  const [openAllBusiness, setOpenAllBusiness] = useState(false);
  const [openReceived, setOpenReceived] = useState(false);
  const [openNotReceived, setOpenNotReceived] = useState(false);
  const [openC, setOpenC] = useState(false);
  const [openAdditionalPayouts, setOpenAdditionalPayouts] = useState(false);
  const [openRecoveries, setOpenRecoveries] = useState(false);

  const formatINR = (amount) =>
    amount != null
      ? amount.toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
        })
      : "₹0.00";

  const giveRequiredUrl = () => {
    if (selectedDsaId !== 0) {
      return {
        receivedUrl: URLs.dmaReceviedBusinessBreakdownUrl,
        notReceivedUrl: URLs.dmaNotReceviedBusinessBreakdownUrl,
        allBusinessUrl: URLs.dmaAllBusinessByMisDate,
      };
    }
    return {
      receivedUrl: URLs.receivedBusinessBreakdownUrl,
      notReceivedUrl: URLs.notReceivedBusinessBreakdownUrl,
      allBusinessUrl: URLs.khAllDisbursedBusinessUrl,
    };
  };

  const labelSx = {
    color: "rgba(255,255,255,0.75)",
    fontSize: isMobile ? "0.55rem" : "0.58rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    position: "relative",
    zIndex: 1,
  };

  const whiteValueSx = {
    color: "#ffffff",
    fontSize: isMobile ? "0.68rem" : "0.75rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    position: "relative",
    zIndex: 1,
  };

  const lightRedValueSx = {
    color: "#fca5a5",
    fontSize: isMobile ? "0.68rem" : "0.75rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    position: "relative",
    zIndex: 1,
  };

  const rowSx = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1.2,
    position: "relative",
    zIndex: 1,
  };

  const clickableRowSx = () => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1.2,
    cursor: "pointer",
    borderRadius: "7px",
    px: 0.8,
    mx: -0.8,
    py: 0.3,
    position: "relative",
    zIndex: 1,
    "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
    transition: "background 0.18s ease",
  });

  const dividerSx = {
    borderColor: "rgba(255,255,255,0.15)",
    my: 0.2,
    position: "relative",
    zIndex: 1,
  };

  const baseShell = {
    borderRadius: "12px",
    p: isMobile ? "8px 10px" : "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    background: "linear-gradient(145deg, #1e3a5f 0%, #2563a8 100%)",
    border: "1px solid #3b7dd8",
    borderTop: "3px solid #60a5fa",
    boxShadow: "0 4px 18px rgba(0,80,160,0.3), inset 0 1px 0 rgba(96,165,250,0.08)",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 28px rgba(0,100,200,0.4)" },
  };

  const card1Sx = { ...baseShell };
  const card2Sx = { ...baseShell, cursor: "pointer" };
  const card3Sx = { ...baseShell, cursor: "pointer" };
  const card4Sx = { ...baseShell, cursor: "pointer" };

  const card5Sx = {
    ...baseShell,
    background: "linear-gradient(145deg, #92400e 0%, #b45309 35%, #d97706 65%, #f59e0b 100%)",
    border: "2px solid rgba(245,158,11,0.6)",
    borderTop: "2px solid rgba(245,158,11,0.6)",
    boxShadow:
      "0 6px 28px rgba(180,120,0,0.4), 0 0 40px rgba(245,158,11,0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 36px rgba(200,140,0,0.5), 0 0 50px rgba(245,158,11,0.25)",
    },
  };

  const centerLabelSx = {
    ...labelSx,
    textAlign: "center",
    fontSize: isMobile ? "0.58rem" : "0.62rem",
  };

  const centerValueSx = {
    ...whiteValueSx,
    textAlign: "center",
    fontSize: isMobile ? "0.82rem" : "0.92rem",
    mt: 0.2,
  };

  const goldLabelSx = {
    color: "rgba(255,255,255,0.85)",
    fontSize: isMobile ? "0.55rem" : "0.58rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    position: "relative",
    zIndex: 1,
  };

  const goldValueSx = {
    color: "#ffffff",
    fontSize: isMobile ? "0.68rem" : "0.75rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    textShadow: "0 1px 4px rgba(0,0,0,0.4)",
    position: "relative",
    zIndex: 1,
  };

  const goldLightRedSx = {
    color: "#fecaca",
    fontSize: isMobile ? "0.68rem" : "0.75rem",
    fontWeight: 800,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    textShadow: "0 1px 4px rgba(0,0,0,0.4)",
    position: "relative",
    zIndex: 1,
  };

  const goldDividerSx = {
    borderColor: "rgba(255,255,255,0.2)",
    my: 0.3,
    position: "relative",
    zIndex: 1,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        width: "100%",
        gap: isMobile ? 1 : 0.8,
        background: "#161616",
        p: isMobile ? 1 : 1.2,
        borderRadius: "16px",
        alignItems: "stretch",
        boxSizing: "border-box",
        m: 0,
        overflowX: "auto",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      {/* ══ CARD 1 — Business Metrics ══ */}
      <Box sx={card1Sx}>
        {/* All Business — uses BusinessBreakdownDialog (default columnDefs) */}
        <Box sx={clickableRowSx()} onClick={() => setOpenAllBusiness(true)}>
          <Typography sx={labelSx}>Total Business</Typography>
          <Typography sx={whiteValueSx}>{formatINR(data?.totalBusiness?.amount)}</Typography>
        </Box>
        <Divider sx={dividerSx} />
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Revenue</Typography>
          <Typography sx={whiteValueSx}>{formatINR(data?.totalExpectedRevenue?.amount)}</Typography>
        </Box>
        {/* Received — uses ReceivedBusinessBreakdownDialog */}
        <Box sx={clickableRowSx()} onClick={() => setOpenReceived(true)}>
          <Typography sx={labelSx}>Received</Typography>
          <Typography sx={whiteValueSx}>{formatINR(data?.receivedRevenue?.amount)}</Typography>
        </Box>
        {/* Not Received — uses ReceivedBusinessBreakdownDialog */}
        <Box sx={clickableRowSx()} onClick={() => setOpenNotReceived(true)}>
          <Typography sx={labelSx}>Not Received</Typography>
          <Typography sx={lightRedValueSx}>{formatINR(data?.notReceivedAmount?.amount)}</Typography>
        </Box>
      </Box>

      {/* ══ CARD 2 — Additional Payouts ══ */}
      <Box sx={card2Sx} onClick={() => setOpenAdditionalPayouts(true)}>
        <Typography sx={centerLabelSx}>Additional Payouts</Typography>
        <Divider sx={dividerSx} />
        <Typography sx={centerValueSx}>{formatINR(data?.additionalPayout?.amount)}</Typography>
      </Box>

      {/* ══ CARD 3 — Recoveries ══ */}
      <Box sx={card3Sx} onClick={() => setOpenRecoveries(true)}>
        <Typography sx={centerLabelSx}>Recoveries</Typography>
        <Divider sx={dividerSx} />
        <Typography sx={centerValueSx}>{formatINR(data?.recoveryAmounts?.amount)}</Typography>
      </Box>

      {/* ══ CARD 4 — Connector Payouts ══ */}
      <Box sx={card4Sx} onClick={() => setOpenC(true)}>
        <Typography sx={centerLabelSx}>Connector Payouts</Typography>
        <Divider sx={dividerSx} />
        <Typography sx={centerValueSx}>{formatINR(data?.connectorPayouts?.amount)}</Typography>
      </Box>

      {/* ── Gold Separator ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "2px",
          minHeight: isMobile ? "2px" : "auto",
          background: "linear-gradient(180deg, transparent, rgba(245,158,11,0.7), transparent)",
          borderRadius: "2px",
          alignSelf: "stretch",
          flexShrink: 0,
          mx: isMobile ? 0 : 0.3,
          my: isMobile ? 0.5 : 0,
        }}
      />

      {/* ══ CARD 5 — Gold-Amber Net Summary ══ */}
      <Box sx={card5Sx}>
        <Box sx={rowSx}>
          <Typography sx={goldLabelSx}>Total Business</Typography>
          <Typography sx={goldValueSx}>{formatINR(data?.totalBusiness?.amount)}</Typography>
        </Box>
        <Divider sx={goldDividerSx} />
        <Box sx={rowSx}>
          <Typography sx={goldLabelSx}>Net Revenue</Typography>
          <Typography sx={goldValueSx}>{formatINR(data?.totalNetBusiness?.amount)}</Typography>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={goldLabelSx}>Net Received</Typography>
          <Typography sx={goldValueSx}>
            {formatINR(data?.totalNetReceivedAmount?.amount)}
          </Typography>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={goldLabelSx}>Not Received</Typography>
          <Typography sx={goldLightRedSx}>{formatINR(data?.notReceivedAmount?.amount)}</Typography>
        </Box>
      </Box>

      {/* ══ DIALOGS ══ */}

      {/* All Business — original BusinessBreakdownDialog with full columnDefs */}
      <BusinessBreakdownDialog
        open={openAllBusiness}
        onClose={() => setOpenAllBusiness(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        isIncomeByMis={isIncomeByMis}
        urlApi={giveRequiredUrl().allBusinessUrl}
        selectedDsaId={selectedDsaId}
        dialogType="allBusiness"
      />

      {/* Received — dedicated dialog with received-only columnDefs */}
      <ReceivedBusinessBreakdownDialog
        open={openReceived}
        onClose={() => setOpenReceived(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        urlApi={giveRequiredUrl().receivedUrl}
        selectedDsaId={selectedDsaId}
        dialogType="received"
      />

      {/* Not Received — dedicated dialog with received-only columnDefs */}
      <ReceivedBusinessBreakdownDialog
        open={openNotReceived}
        onClose={() => setOpenNotReceived(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        urlApi={giveRequiredUrl().notReceivedUrl}
        selectedDsaId={selectedDsaId}
        dialogType="notReceived"
      />

      <ConnectorPaymentsDialog
        open={openC}
        onClose={() => setOpenC(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
      />
      <AdditionalPayoutsDialog
        open={openAdditionalPayouts}
        onClose={() => setOpenAdditionalPayouts(false)}
        startDate={startDate}
        endDate={endDate}
        selectedDsaId={selectedDsaId}
      />
      <RecoveriesDialog
        open={openRecoveries}
        onClose={() => setOpenRecoveries(false)}
        startDate={startDate}
        endDate={endDate}
        selectedDsaId={selectedDsaId}
      />
    </Box>
  );
};

BusinessSummaryBar.propTypes = {
  data: PropTypes.shape({
    totalExpectedRevenue: PropTypes.shape({ amount: PropTypes.number }),
    receivedRevenue: PropTypes.shape({ amount: PropTypes.number }),
    notReceivedAmount: PropTypes.shape({ amount: PropTypes.number }),
    totalBusiness: PropTypes.shape({ amount: PropTypes.number }),
    additionalPayout: PropTypes.shape({ amount: PropTypes.number }),
    recoveryAmounts: PropTypes.shape({ amount: PropTypes.number }),
    totalNetBusiness: PropTypes.shape({ amount: PropTypes.number }),
    totalNetReceivedAmount: PropTypes.shape({ amount: PropTypes.number }),
    connectorPayouts: PropTypes.shape({ amount: PropTypes.number }),
  }).isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  isIncomeByMis: PropTypes.bool,
  setAlert: PropTypes.func.isRequired,
  selectedDsaId: PropTypes.number,
};

export default BusinessSummaryBar;
